import React, { useMemo } from 'react';
import type { ClockFormat } from '@/types/clock';

interface AnalogClockProps {
  time: Date;
  showSeconds?: boolean;
  showDate?: boolean;
  showTimeZone?: boolean;
  timeZoneName?: string;
  textColor?: string;
  accentColor?: string;
  style?: 'luxury' | 'minimal' | 'swiss' | 'deco';
}

export const AnalogClock: React.FC<AnalogClockProps> = React.memo(({
  time,
  showSeconds = true,
  showDate = false,
  showTimeZone = false,
  timeZoneName,
  textColor = '#ffffff',
  accentColor = '#fbbf24',
  style = 'luxury'
}) => {
  const { hours, minutes, seconds } = useMemo(() => {
    const h = time.getHours() % 12;
    const m = time.getMinutes();
    const s = time.getSeconds();
    
    return {
      hours: (h * 30) + (m * 0.5), // 30 degrees per hour + 0.5 degrees per minute
      minutes: (m * 6) + (s * 0.1), // 6 degrees per minute + 0.1 degrees per second
      seconds: s * 6 // 6 degrees per second
    };
  }, [time]);

  const getLuxuryMarkers = () => (
    <>
      {[...Array(12)].map((_, i) => {
        const angle = (i * 30) - 90;
        const isQuarter = i % 3 === 0;
        const radius = isQuarter ? 85 : 90;
        const x = 100 + radius * Math.cos(angle * Math.PI / 180);
        const y = 100 + radius * Math.sin(angle * Math.PI / 180);
        
        return (
          <g key={i}>
            {/* Hour markers */}
            <circle
              cx={x}
              cy={y}
              r={isQuarter ? 3 : 1.5}
              fill={textColor}
              opacity={isQuarter ? 1 : 0.6}
            />
            {/* Roman numerals for quarters */}
            {isQuarter && (
              <text
                x={100 + 72 * Math.cos(angle * Math.PI / 180)}
                y={100 + 72 * Math.sin(angle * Math.PI / 180)}
                fill={textColor}
                fontSize="14"
                fontWeight="300"
                textAnchor="middle"
                dominantBaseline="middle"
                opacity="0.8"
              >
                {['XII', 'III', 'VI', 'IX'][i / 3]}
              </text>
            )}
          </g>
        );
      })}
    </>
  );

  const getMinimalMarkers = () => (
    <>
      {[...Array(12)].map((_, i) => {
        const angle = (i * 30) - 90;
        const isQuarter = i % 3 === 0;
        const x1 = 100 + 90 * Math.cos(angle * Math.PI / 180);
        const y1 = 100 + 90 * Math.sin(angle * Math.PI / 180);
        const x2 = 100 + (isQuarter ? 80 : 85) * Math.cos(angle * Math.PI / 180);
        const y2 = 100 + (isQuarter ? 80 : 85) * Math.sin(angle * Math.PI / 180);
        
        return (
          <line
            key={i}
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            stroke={textColor}
            strokeWidth={isQuarter ? 2 : 1}
            opacity={isQuarter ? 1 : 0.5}
            strokeLinecap="round"
          />
        );
      })}
    </>
  );

  const getSwissMarkers = () => (
    <>
      {/* Minute markers */}
      {[...Array(60)].map((_, i) => {
        const angle = (i * 6) - 90;
        const isHour = i % 5 === 0;
        const x1 = 100 + 90 * Math.cos(angle * Math.PI / 180);
        const y1 = 100 + 90 * Math.sin(angle * Math.PI / 180);
        const x2 = 100 + (isHour ? 78 : 87) * Math.cos(angle * Math.PI / 180);
        const y2 = 100 + (isHour ? 78 : 87) * Math.sin(angle * Math.PI / 180);
        
        if (i % 5 !== 0 && i % 1 === 0) {
          return (
            <line
              key={i}
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke={textColor}
              strokeWidth={0.5}
              opacity={0.3}
            />
          );
        }
        
        return isHour ? (
          <rect
            key={i}
            x={x2 - 1.5}
            y={y2 - 6}
            width={3}
            height={12}
            fill={textColor}
            transform={`rotate(${angle + 90} ${x2} ${y2})`}
          />
        ) : null;
      })}
    </>
  );

  const getDecoMarkers = () => (
    <>
      {[...Array(12)].map((_, i) => {
        const angle = (i * 30) - 90;
        const x = 100 + 75 * Math.cos(angle * Math.PI / 180);
        const y = 100 + 75 * Math.sin(angle * Math.PI / 180);
        
        return (
          <g key={i}>
            {/* Diamond shapes */}
            <path
              d={`M ${x} ${y - 5} L ${x + 4} ${y} L ${x} ${y + 5} L ${x - 4} ${y} Z`}
              fill={textColor}
              opacity={0.8}
            />
            {/* Numbers */}
            <text
              x={100 + 60 * Math.cos(angle * Math.PI / 180)}
              y={100 + 60 * Math.sin(angle * Math.PI / 180)}
              fill={textColor}
              fontSize="16"
              fontWeight="500"
              fontFamily="serif"
              textAnchor="middle"
              dominantBaseline="middle"
              opacity="0.9"
            >
              {i === 0 ? 12 : i}
            </text>
          </g>
        );
      })}
    </>
  );

  const renderMarkers = () => {
    switch (style) {
      case 'luxury': return getLuxuryMarkers();
      case 'minimal': return getMinimalMarkers();
      case 'swiss': return getSwissMarkers();
      case 'deco': return getDecoMarkers();
      default: return getLuxuryMarkers();
    }
  };

  return (
    <div className="flex flex-col items-center justify-center h-full w-full pt-6">
      {showTimeZone && timeZoneName && (
        <div className="text-lg font-medium mb-6" style={{ color: textColor, opacity: 0.8 }}>
          {timeZoneName}
        </div>
      )}
      
      <svg
        viewBox="0 0 200 200"
        className="w-full max-w-[280px] h-auto drop-shadow-2xl"
        style={{ filter: 'drop-shadow(0 10px 30px rgba(0, 0, 0, 0.3))' }}
      >
        {/* Outer ring */}
        <circle
          cx="100"
          cy="100"
          r="95"
          fill="none"
          stroke={textColor}
          strokeWidth="0.5"
          opacity="0.3"
        />
        
        {/* Inner ring */}
        <circle
          cx="100"
          cy="100"
          r="92"
          fill="none"
          stroke={textColor}
          strokeWidth="0.5"
          opacity="0.2"
        />

        {/* Markers */}
        {renderMarkers()}

        {/* Hour hand */}
        <line
          x1="100"
          y1="100"
          x2="100"
          y2="55"
          stroke={textColor}
          strokeWidth="4"
          strokeLinecap="round"
          opacity="0.9"
          style={{
            transform: `rotate(${hours}deg)`,
            transformOrigin: '100px 100px',
            transition: 'transform 1s cubic-bezier(0.4, 0, 0.2, 1)'
          }}
        />

        {/* Minute hand */}
        <line
          x1="100"
          y1="100"
          x2="100"
          y2="35"
          stroke={textColor}
          strokeWidth="3"
          strokeLinecap="round"
          opacity="0.95"
          style={{
            transform: `rotate(${minutes}deg)`,
            transformOrigin: '100px 100px',
            transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)'
          }}
        />

        {/* Second hand */}
        {showSeconds && (
          <line
            x1="100"
            y1="110"
            x2="100"
            y2="25"
            stroke={accentColor}
            strokeWidth="1.5"
            strokeLinecap="round"
            opacity="1"
            style={{
              transform: `rotate(${seconds}deg)`,
              transformOrigin: '100px 100px',
              transition: 'transform 0.05s linear'
            }}
          />
        )}

        {/* Center dot */}
        <circle cx="100" cy="100" r="5" fill={accentColor} opacity="1" />
        <circle cx="100" cy="100" r="3" fill={textColor} opacity="0.3" />
      </svg>

      {showDate && (
        <div className="text-base font-medium mt-4" style={{ color: textColor, opacity: 0.6 }}>
          {time.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
        </div>
      )}
    </div>
  );
});

AnalogClock.displayName = 'AnalogClock';
